import { isEqual } from "lodash";
import { DataTypes } from "../../../data/DataManager";
import { Site } from "../../common/external-sources/Sites";
import { FieldSchema, FieldValue, FieldValues, isEmpty } from "../../../Schema";
import { Modes } from "../../common/fields/mode";
import { EntryTypes } from "../../common/fields/entryType";


export const fields4ep = [
  "linkedInstance",
  "siteName",
  "serialNumber",
  "equipmentId",
  "materialNumber",
  "instrumentGTIN",
  "entryType",
  "cluster",
  "belongingToGroup",
  "buildingLocation",
  "floor",
  "room",
  "functionalLocation",
  "costCenter",
  "gxpRelevant",
  "qualificationStatus",
  "controlledEquipmentStatus",
  "qualificationDocuments",
  "softwareVersion",
  "configurationBaseline",
  "configurationDetails",
];

export const sites4ep =  ["Ludwigsburg", "Mannheim", "Penzberg"];

/** The maturity classifications whose V&V status changes need a second pair of eyes. */
export const vnvClassifications4ep = ["Pilot", "Series"];

const prepareValuesFor4ep = (values: FieldValues): FieldValues => {
  const preparedValues: FieldValues = {};
  for (const field of fields4ep) {
    if (!isEmpty(values[field])) {
      preparedValues[field] = values[field];
    }
  }
  return preparedValues;
};


const resolveValueAfterSave = (submitted: FieldValue, stored: FieldValue): FieldValue =>
  submitted === undefined ? stored : submitted;

/**
 * Whether this save changes a V&V device's status while it is (or becomes) Pilot/Series.
 *
 * OR-ed with the clause below rather than folded into it: V&V equipment — group "V&V", Rotkreuz,
 * gxpRelevant "No" — meets none of that clause's preconditions. Both fields are read as they
 * stand after the save, so promoting to Pilot and moving the status together still gates.
 * Inert for non-V&V equipment, which carries neither field.
 */
const isVnv4epChange = (values: FieldValues, original: FieldValues): boolean =>
  !isEqual(resolveValueAfterSave(values.vnvStatus, original.vnvStatus), original.vnvStatus) &&
  vnvClassifications4ep.includes(
    String(resolveValueAfterSave(
      values.vnvMaturityClassification,
      original.vnvMaturityClassification
    ))
  );

export const is4epMode: FieldSchema = {
  name: "is4epMode",
  display: {
    displayed: false
  },
  graphql: {
    selection: undefined,
  },
  dataTransform: {
    fromGraphql: false,
    // Never persisted, by either route: this exists to drive the form and the schema's
    // own cross-field logic, not to be stored.
    toGraphql: undefined,
    toDb: undefined,
  },
  data: {
    type: DataTypes.BOOLEAN,
    assignedValue: {
      getter: (values) => {
        const preparedInitialValues = prepareValuesFor4ep(
          (values.originalValues || {}) as FieldValues
        );
        const preparedValues = prepareValuesFor4ep({
          ...preparedInitialValues,
          ...values,
        });
        if (
          values.originalValues &&
          [Modes.EDIT, Modes.BULK_EDIT]
            .includes(values.mode as Modes)
        ) {
          return Boolean(
            (
              (
                !isEqual(
                  preparedValues,
                  preparedInitialValues
                ) &&
                String(preparedInitialValues.belongingToGroup).startsWith("DSR") &&
                sites4ep.includes(String((preparedInitialValues.siteName as Site)?.siteName)) &&
                preparedInitialValues.entryType !== EntryTypes.CLUSTER &&
                (
                  preparedInitialValues.gxpRelevant === "Yes" ||
                  preparedValues.gxpRelevant === "Yes"
                )
              ) ||
              (
                preparedInitialValues.systemStatus !== preparedValues.systemStatus &&
                preparedValues.systemStatus === "In service"
              )
            ) ||
            isVnv4epChange(values, (values.originalValues || {}) as FieldValues)
          );
        }
        if (values.mode === Modes.CREATE) {
          return String(preparedValues.belongingToGroup).startsWith("DSR") &&
            sites4ep.includes(String((preparedValues.siteName as Site)?.siteName)) &&
            preparedValues.entryType !== EntryTypes.CLUSTER;
        }
        return undefined;
      },
      // The V&V fields are dependencies but NOT members of `fields4ep`, and the two lists are
      // not interchangeable. `fields4ep` is the standard clause's change-detection surface:
      // adding them there would let a V&V status change satisfy that clause on a DSR/site/GxP
      // record, bypassing the Pilot/Series gate. Adding them here is required — the getter is
      // handed only what this list names, and the same list decides when it recomputes, so
      // leaving them out means `isVnv4epChange` reads `undefined` and never re-runs.
      fields: [
        ...fields4ep,
        "vnvStatus",
        "vnvMaturityClassification",
        "mode",
        "originalValues",
      ],
    }
  },
  form: {
    label: "Is 4ep mode?",
    disabled: true,
    displayed: false,
  }
};